const REFRESH_LOCK_NAME = 'his-auth-token-refresh' as const;
const LOCK_WAIT_TIMEOUT_MS = 5_000;

const isAbortError = (error: unknown) => {
  if (error instanceof DOMException) {
    return error.name === 'AbortError';
  }

  return (
    typeof error === 'object' &&
    error !== null &&
    'name' in error &&
    error.name === 'AbortError'
  );
};

export const withRefreshLock = async <T>(run: () => Promise<T>): Promise<T> => {
  const locks =
    typeof navigator === 'undefined' ||
    // oxlint-disable-next-line typescript/no-unnecessary-condition -- Web Locks API is not everywhere; optional chain is required at runtime
    typeof navigator.locks?.request !== 'function'
      ? null
      : navigator.locks;

  if (!locks) {
    return run();
  }

  let granted = false;
  const controller = new AbortController();
  const timeoutId = window.setTimeout(
    () => controller.abort(),
    LOCK_WAIT_TIMEOUT_MS,
  );

  try {
    return await locks.request(
      REFRESH_LOCK_NAME,
      { mode: 'exclusive', signal: controller.signal },
      async () => {
        granted = true;
        window.clearTimeout(timeoutId);

        return run();
      },
    );
  } catch (error) {
    // oxlint-disable-next-line typescript/no-unnecessary-condition -- mutated inside locks.request callback
    if (granted) {
      throw error;
    }

    if (!(controller.signal.aborted && isAbortError(error))) {
      throw error;
    }

    console.warn(
      'Web Lock wait timed out; running token refresh without a lock',
    );

    return run();
  } finally {
    window.clearTimeout(timeoutId);
  }
};
